import { useState, useEffect } from "react";
import { useGameStore } from "../store/gameStore";
import { useApi } from "../hooks/useApi";
import { hapticFeedback } from "../telegram/init";
import { SlotDetailPanel } from "../components/panels/SlotDetailPanel";
import { DonationPanel } from "../components/panels/DonationPanel";

interface IslandData {
  id: number;
  name: string;
  sectorX: number;
  sectorY: number;
  luxuryResource: string;
  forestLevel: number;
  mineLevel: number;
  forestDonated: number;
  mineDonated: number;
  forestNextLevel: number;
  mineNextLevel: number;
  slots: {
    position: number;
    cityId: number | null;
    cityName: string | null;
    playerName: string | null;
    playerId: number | null;
  }[];
}

type DonationType = "FOREST" | "MINE";

const LUXURY_ICONS: Record<string, string> = {
  WINE:    "\u{1F377}",
  MARBLE:  "\u2B1C",
  CRYSTAL: "\u{1F48E}",
  SULFUR:  "\u{1F525}",
};

// Compute positions for n slots evenly distributed on an ellipse,
// starting from the top, going clockwise.
// Returns {x, y} as percentages of the container dimensions.
function ovalPositions(n: number, rx: number, ry: number) {
  return Array.from({ length: n }, (_, i) => {
    const angle = (i / n) * 2 * Math.PI - Math.PI / 2;
    return {
      x: 50 + rx * Math.cos(angle),
      y: 50 + ry * Math.sin(angle),
    };
  });
}

// Two rings of 8 for 16 slots total — avoids overlap on small screens.
const outerPositions = ovalPositions(8, 42, 36); // slots 0-7
const innerPositions = ovalPositions(8, 24, 20); // slots 8-15

function getSlotPosition(position: number) {
  return position < 8
    ? outerPositions[position]
    : innerPositions[position - 8];
}

export function IslandScreen() {
  const cities       = useGameStore((s) => s.cities);
  const activeCityId = useGameStore((s) => s.activeCityId);
  const user         = useGameStore((s) => s.user);
  const api          = useApi();

  const [island, setIsland]           = useState<IslandData | null>(null);
  const [loading, setLoading]         = useState(true);
  const [selectedSlot, setSelectedSlot] = useState<IslandData["slots"][0] | null>(null);
  const [donationType, setDonationType] = useState<DonationType | null>(null);

  const city = cities.find((c) => c.id === activeCityId);

  function fetchIsland() {
    if (!city?.islandId) return;
    api.getIsland(city.islandId)
      .then(setIsland)
      .catch(console.error);
  }

  useEffect(() => {
    if (!city?.islandId) return;
    setLoading(true);
    api.getIsland(city.islandId)
      .then(setIsland)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [city?.islandId]);

  if (loading) {
    return (
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "var(--space-lg)",
        background: "linear-gradient(180deg, #0d2e45 0%, #1a5276 100%)",
      }}>
        <span style={{ fontSize: "2.5rem", animation: "float 2s ease infinite" }}>
          {"\u{1F3DD}\uFE0F"}
        </span>
        <span style={{
          fontFamily: "var(--font-display)",
          color: "rgba(250,243,230,0.65)",
          fontSize: "0.78rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}>
          Discovering island…
        </span>
      </div>
    );
  }

  if (!island) {
    return (
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(180deg, #0d2e45, #1a5276)",
        color: "var(--papyrus-light)",
      }}>
        No island data.
      </div>
    );
  }

  const occupiedCount = island.slots.filter((s) => s.cityId !== null).length;
  const luxuryIcon    = LUXURY_ICONS[island.luxuryResource] ?? "\u2728";

  return (
    <div style={{
      flex: 1,
      position: "relative",
      overflow: "hidden",
      background: "linear-gradient(170deg, #0d2e45 0%, #1a5276 40%, #1e6fa3 70%, #2980b9 100%)",
    }}>

      {/* — Animated sea shimmer — */}
      <div style={{
        position: "absolute",
        inset: 0,
        background: "repeating-linear-gradient(90deg, transparent, transparent 60px, rgba(255,255,255,0.014) 60px, rgba(255,255,255,0.014) 120px)",
        animation: "shimmer 10s linear infinite",
        backgroundSize: "200% 100%",
        pointerEvents: "none",
      }} />

      {/* — Subtle horizontal wave lines — */}
      <div style={{
        position: "absolute",
        inset: 0,
        background: "repeating-linear-gradient(0deg, transparent, transparent 44px, rgba(255,255,255,0.018) 44px, rgba(255,255,255,0.018) 45px)",
        pointerEvents: "none",
      }} />

      {/* — Floating header — */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        padding: "var(--space-md) var(--space-lg)",
        background: "linear-gradient(180deg, rgba(13,46,69,0.92) 0%, transparent 100%)",
        zIndex: 10,
        paddingBottom: "var(--space-2xl)",
      }}>
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}>
          <div>
            <div style={{
              fontFamily: "var(--font-display)",
              fontSize: "1rem",
              fontWeight: 700,
              color: "var(--papyrus-light)",
              letterSpacing: "0.04em",
            }}>
              {island.name}
            </div>
            <div style={{
              display: "flex",
              gap: "var(--space-md)",
              marginTop: "3px",
              fontSize: "0.7rem",
              color: "rgba(250,243,230,0.55)",
            }}>
              <span>{"\u{1F465}"} {occupiedCount}/{island.slots.length}</span>
              <span>[{island.sectorX}:{island.sectorY}]</span>
            </div>
          </div>
          <div style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.7rem",
            color: "rgba(250,243,230,0.65)",
            textAlign: "right",
            lineHeight: 1.6,
          }}>
            <div>{luxuryIcon} {island.luxuryResource.charAt(0) + island.luxuryResource.slice(1).toLowerCase()}</div>
          </div>
        </div>
      </div>

      {/* — Map area: island + slots — */}
      <div style={{
        position: "absolute",
        inset: 0,
        paddingTop: "52px",
        paddingBottom: "56px",
      }}>
        {/* Island landmass */}
        <IslandShape />

        {/* City slots — two rings */}
        {island.slots.map((slot) => {
          const pos    = getSlotPosition(slot.position);
          const isOwn  = slot.playerId !== null && slot.playerId === user?.id;
          const occupied = slot.cityId !== null;

          return (
            <button
              key={slot.position}
              onClick={() => {
                hapticFeedback("light");
                setSelectedSlot(slot);
              }}
              style={{
                position: "absolute",
                left: `${pos.x}%`,
                top: `${pos.y}%`,
                transform: "translate(-50%, -50%)",
                width: "46px",
                height: "46px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: isOwn
                  ? "2.5px solid var(--gold)"
                  : occupied
                    ? "2px solid rgba(250,243,230,0.5)"
                    : "1.5px dashed rgba(250,243,230,0.22)",
                background: isOwn
                  ? "linear-gradient(135deg, rgba(201,169,97,0.38), rgba(218,183,82,0.22))"
                  : occupied
                    ? "rgba(250,243,230,0.14)"
                    : "rgba(255,255,255,0.05)",
                backdropFilter: "blur(5px)",
                WebkitBackdropFilter: "blur(5px)",
                animation: isOwn
                  ? `island-fade-in 0.4s ease both ${slot.position * 35}ms, gold-glow 2.5s ease infinite`
                  : `island-fade-in 0.4s ease both ${slot.position * 35}ms`,
                padding: 0,
                cursor: "pointer",
                zIndex: 5,
              }}
            >
              {occupied ? (
                <span style={{ fontSize: "0.95rem", lineHeight: 1 }}>
                  {isOwn ? "\u{1F451}" : "\u{1F3E0}"}
                </span>
              ) : (
                <span style={{ fontSize: "0.75rem", opacity: 0.28, lineHeight: 1 }}>
                  {"\u2795"}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* — Bottom info strip (tappable — opens donation panel) — */}
      <div style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: "var(--space-sm) var(--space-lg)",
        background: "linear-gradient(0deg, rgba(13,46,69,0.88) 0%, transparent 100%)",
        display: "flex",
        justifyContent: "center",
        gap: "var(--space-md)",
        zIndex: 10,
        paddingTop: "var(--space-2xl)",
      }}>
        <DonationPill
          icon="\u{1F332}"
          label="Sawmill"
          level={island.forestLevel}
          donated={island.forestDonated}
          nextLevel={island.forestNextLevel}
          onClick={() => { hapticFeedback("medium"); setDonationType("FOREST"); }}
        />
        <DonationPill
          icon={luxuryIcon}
          label={island.luxuryResource.charAt(0) + island.luxuryResource.slice(1).toLowerCase() + " Mine"}
          level={island.mineLevel}
          donated={island.mineDonated}
          nextLevel={island.mineNextLevel}
          onClick={() => { hapticFeedback("medium"); setDonationType("MINE"); }}
        />
      </div>

      {/* — Panels — */}
      {selectedSlot && (
        <SlotDetailPanel
          slot={selectedSlot}
          user={user}
          onClose={() => setSelectedSlot(null)}
        />
      )}
      {donationType && (
        <DonationPanel
          island={island}
          type={donationType}
          onClose={() => setDonationType(null)}
          onSuccess={() => {
            setDonationType(null);
            fetchIsland();
          }}
        />
      )}
    </div>
  );
}

// — Organic island landmass —
function IslandShape() {
  return (
    <div style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "120px",
      height: "100px",
      zIndex: 3,
      pointerEvents: "none",
    }}>
      {/* Ambient water glow */}
      <div style={{
        position: "absolute",
        inset: "-24px",
        borderRadius: "50%",
        background: "radial-gradient(ellipse, rgba(41,128,185,0.3) 0%, transparent 68%)",
        filter: "blur(10px)",
      }} />
      {/* Landmass */}
      <div style={{
        position: "absolute",
        inset: 0,
        borderRadius: "58% 42% 55% 45% / 52% 61% 39% 48%",
        background: "linear-gradient(145deg, #4a6741 0%, #5b7553 45%, #3d5434 100%)",
        boxShadow: "0 5px 24px rgba(0,0,0,0.55), inset 0 2px 0 rgba(255,255,255,0.07)",
      }} />
      {/* Beach highlight */}
      <div style={{
        position: "absolute",
        inset: "5px",
        borderRadius: "58% 42% 55% 45% / 52% 61% 39% 48%",
        background: "linear-gradient(145deg, rgba(212,196,160,0.22) 0%, transparent 55%)",
        pointerEvents: "none",
      }} />
      {/* Trees */}
      <div style={{ position: "absolute", top: "18px", left: "24px",  fontSize: "0.8rem" }}>{"\u{1F332}"}</div>
      <div style={{ position: "absolute", top: "34px", left: "50px",  fontSize: "1rem"  }}>{"\u{1F333}"}</div>
      <div style={{ position: "absolute", top: "16px", right: "22px", fontSize: "0.7rem" }}>{"\u{1F332}"}</div>
      <div style={{ position: "absolute", bottom: "18px", left: "36px",  fontSize: "0.65rem" }}>{"\u{1F333}"}</div>
      <div style={{ position: "absolute", bottom: "20px", right: "28px", fontSize: "0.75rem" }}>{"\u{1F332}"}</div>
    </div>
  );
}

// — Tappable donation info pill —
interface DonationPillProps {
  icon: string;
  label: string;
  level: number;
  donated: number;
  nextLevel: number;
  onClick: () => void;
}

function DonationPill({ icon, label, level, donated, nextLevel, onClick }: DonationPillProps) {
  const progress = nextLevel > 0 ? Math.min(donated / nextLevel, 1) : 0;

  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "4px",
        padding: "8px 12px",
        borderRadius: "var(--radius)",
        background: "rgba(255,255,255,0.1)",
        border: "1px solid rgba(255,255,255,0.12)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        minWidth: "130px",
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "6px", width: "100%" }}>
        <span style={{ fontSize: "0.85rem" }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.65rem",
            color: "var(--papyrus-light)",
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}>
            {label}
          </div>
          <div style={{
            fontSize: "0.58rem",
            color: "rgba(250,243,230,0.5)",
          }}>
            Level {level} — tap to donate
          </div>
        </div>
      </div>
      {/* Mini progress bar */}
      <div style={{
        width: "100%",
        height: "3px",
        borderRadius: "2px",
        background: "rgba(255,255,255,0.12)",
        overflow: "hidden",
      }}>
        <div style={{
          height: "100%",
          width: `${progress * 100}%`,
          borderRadius: "2px",
          background: "linear-gradient(90deg, var(--gold-dim), var(--gold-bright))",
          transition: "width 0.4s ease",
        }} />
      </div>
    </button>
  );
}
